import { Details, Summary } from '@/components/Atoms/Details/Details';
import { NeutralLink } from '@/components/Atoms/Neutral/Neutral';
import { H5 } from '@/components/Core/Heading';
import type { IAnalyticsTraceableElement } from '@/interfaces';
import { buildTrackingObject } from '@/services/Analytics/bbAnalyticsTrackers';
import { TrackingAction } from '@/services/Analytics/bnefAnalyticsSettings';
import type { MouseEvent, ReactNode } from 'react';
import { useState } from 'react';
import type { FCh } from 'react-fc';
import styled from 'styled-components';

/**
 * An expandable element that can be triggered to display some content.
 */
export const ExpandableDetail: FCh<IProps> = ({
    title,
    closeTitle,
    href,
    children,
    className,
    analyticsBaseId,
    analyticsCategory,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    let displayLabel = <H5 as="span">{isExpanded ? closeTitle || title : title}</H5>;
    if (href) {
        displayLabel = (
            <NeutralLink
                href={href}
                onClick={(evt: MouseEvent<HTMLAnchorElement>) => {
                    // prevent the link from loading, as it only for a fallback / open-in-new-tab support
                    evt.preventDefault();
                    // simulate the click as being on the parent, which should open up the summary
                    evt.currentTarget.parentElement.click();
                }}
            >
                {displayLabel}
            </NeutralLink>
        );
    }

    return (
        <Details
            className={className}
            data-analytics-tracking={buildTrackingObject(
                isExpanded ? TrackingAction.Close : TrackingAction.Open,
                analyticsCategory,
                `${analyticsBaseId}`
            )}
            onOpened={() => setIsExpanded(true)}
            onClosed={() => setIsExpanded(false)}
        >
            <Summary>{displayLabel}</Summary>
            <Inner>{children}</Inner>
        </Details>
    );
};

interface IProps extends IAnalyticsTraceableElement {
    title: ReactNode;
    closeTitle?: string;
    href?: string;
    className?: string;
}

const Inner = styled.div`
    padding: var(--size-grid-unit) 0 var(--size-grid-half);
`;
